import { useState, useEffect, useMemo } from "react";
import { HttpNoData, HttpData } from "../../../../Core";
import {
  Alert,
  Button,
  Col,
  Form,
  InputGroup,
  ListGroup,
  Row,
  Spinner,
  Stack,
  Tab,
  Tabs,
} from "react-bootstrap";
import { MdEdit } from "react-icons/md";
import { HiPlus } from "react-icons/hi";
import styles from "./style.module.css";
import TypeModalCreate from "./TypeModalCreate";
import TypeModalEdit from "./TypeModalEdit";
import CoatingModalCreate from "./CoatingModalCreate";
import CoatingModalEdit from "./CoatingModalEdit";
import CategoryModalCreate from "./CategoryModalCreate";
import CategoryModalEdit from "./CategoryModalEdit";
import ProductModalCreate from "./ProductModalCreate";
import ProductModalEdit from "./ProductModalEdit";
import MaterialModalCreate from "./MaterialModalCreate";
import MaterialModalEdit from "./MaterialModalEdit";
import ProductSubtypeModalEdit from "./ProductSubtypeModalEdit";
import ProductSubtypeModalCreate from "./ProductSubtypeModalCreate";
import AdditionInOrderModalCreate from "./AdditionInOrderModalCreate";
import AdditionOrderModalEdit from "./AdditionInOrderModalEdit";
import ModalNdsEdit from "./ModalNdsEdit";
import { FaAngleDoubleUp, FaAngleDoubleDown } from "react-icons/fa";
import AdditionInProductEdit from "./AdditionInProductEdit";
import { Modal } from "react-bootstrap";

export default function Main({ jwt }) {
  const [typeCategories, setTypeCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [additionsInOrder, setAdditionsInOrder] = useState([]);

  const [priceSettings, setPriceSettings] = useState([]);

  const [createClass, setCreateClass] = useState();
  const [editClass, setEditClass] = useState("");

  const [isNds, setIsNds] = useState(false);
  const [coatings, setCoatings] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [materials, setMaterials] = useState([]);

  const [deliveryMarkupPercent, setDeliveryMarkupPercent] = useState(0);
  const [deliveryMarkupInput, setDeliveryMarkupInput] = useState(0);
  const [additionsInProduct, setAdditionsInProduct] = useState([]);
  const [additionsProductOpen, setAdditionsProductOpen] = useState(false);
  const [additionsProductLoaded, setAdditionsProductLoaded] = useState(false);
  const [editingAdditionProduct, setEditingAdditionProduct] = useState(null);
  const [newAdditionName, setNewAdditionName] = useState('');
  const [newAdditionPrice, setNewAdditionPrice] = useState('');
  const [newAdditionDescription, setNewAdditionDescription] = useState('');
  const [newAdditionIsForExport, setNewAdditionIsForExport] = useState(true);
  const [newAdditionHasQuantity, setNewAdditionHasQuantity] = useState(false);
  const [createAdditionType, setCreateAdditionType] = useState(null);

  const [activeTab, setActiveTab] = useState('prices');
  const [loadedTabs, setLoadedTabs] = useState(new Set(['prices']));
  const [tabLoading, setTabLoading] = useState(false);
  const [typesDataLoaded, setTypesDataLoaded] = useState(false);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  useEffect(() => {
    async function loadPrices() {
      const res = await HttpNoData('/api/v1/calculateOrderCost/priceOnWeight', 'GET', jwt);
      setPriceSettings(res.data ?? []);
    }
    loadPrices();
  }, []);

  async function checkAndFixDuplicates(array, urlBuilder) {
    const duplicates = array.some(
      (item, index) =>
        array.findIndex((x) => x.priority === item.priority) !== index,
    );

    if (duplicates) {
      console.log("Найдены дубликаты");
      return await arrayFixPriority(array, urlBuilder);
    }
    return array;
  }

  const loadTabData = async (tab) => {
    switch (tab) {
      case 'types': {
        const [typesRes, categoriesRes] = await Promise.all([
          HttpNoData('/api/v1/calculateOrderCost/typeProductCategories', 'GET', jwt),
          HttpNoData('/api/v1/calculateOrderCost/productCategories', 'GET', jwt),
        ]);
        const sortedTypes = typesRes.data?.sort((a, b) => a.priority - b.priority) ?? [];
        const sortedCategories = categoriesRes.data?.sort((a, b) => a.priority - b.priority) ?? [];
        const fixedTypes = await checkAndFixDuplicates(sortedTypes, (id, p) =>
          `/api/v1/calculateOrderCost/typeProductCategories/priority/${id}/${p}`
        );
        const fixedCategories = await checkAndFixDuplicates(sortedCategories, (id, p) =>
          `/api/v1/calculateOrderCost/productCategories/priority/${id}/${p}`
        );
        setTypeCategories(fixedTypes);
        setCategories(fixedCategories);
        setTypesDataLoaded(true);
        break;
      }
      case 'references': {
        const [subtypesRes, materialsRes, coatingsRes] = await Promise.all([
          HttpNoData('/api/v1/calculateOrderCost/productSubtypes', 'GET', jwt),
          HttpNoData('/api/v1/calculateOrderCost/materials', 'GET', jwt),
          HttpNoData('/api/v1/calculateOrderCost/coatings', 'GET', jwt),
        ]);
        setSubtypes(subtypesRes.data ?? []);
        setMaterials(materialsRes.data ?? []);
        setCoatings(coatingsRes.data ?? []);
        break;
      }
      case 'orderAdditions': {
        const [res, markupRes] = await Promise.all([
          HttpNoData('/api/v1/calculateOrderCost/orders/additions', 'GET', jwt),
          HttpNoData('/api/v1/calculateOrderCost/deliveryMarkup', 'GET', jwt),
        ]);
        const sorted = res.data?.sort((a, b) => a.priority - b.priority) ?? [];
        const fixed = await checkAndFixDuplicates(sorted, (id, p) =>
          `/api/v1/calculateOrderCost/orders/additions/priority/${id}/${p}`
        );
        setAdditionsInOrder(fixed);
        const percent = markupRes.data?.percent ?? 0;
        setDeliveryMarkupPercent(percent);
        setDeliveryMarkupInput(percent);
        break;
      }
      case 'products': {
        const needTypes = !typesDataLoaded;
        const needReferences = subtypes.length === 0 || materials.length === 0;
        const requests = [
          HttpNoData('/api/v1/calculateOrderCost/productCategories', 'GET', jwt),
          needReferences ? HttpNoData('/api/v1/calculateOrderCost/productSubtypes', 'GET', jwt) : Promise.resolve(null),
          needReferences ? HttpNoData('/api/v1/calculateOrderCost/materials', 'GET', jwt) : Promise.resolve(null),
        ];
        if (needTypes) {
          requests.push(HttpNoData('/api/v1/calculateOrderCost/typeProductCategories', 'GET', jwt));
        }
        const results = await Promise.all(requests);
        const sortedCategories = results[0].data?.sort((a, b) => a.priority - b.priority) ?? [];
        const fixedCategories = await checkAndFixDuplicates(sortedCategories, (id, p) =>
          `/api/v1/calculateOrderCost/productCategories/priority/${id}/${p}`
        );
        setCategories(fixedCategories);
        if (needReferences) {
          setSubtypes(results[1]?.data ?? []);
          setMaterials(results[2]?.data ?? []);
        }
        if (needTypes) {
          const sortedTypes = results[3].data?.sort((a, b) => a.priority - b.priority) ?? [];
          const fixedTypes = await checkAndFixDuplicates(sortedTypes, (id, p) =>
            `/api/v1/calculateOrderCost/typeProductCategories/priority/${id}/${p}`
          );
          setTypeCategories(fixedTypes);
          setTypesDataLoaded(true);
          setLoadedTabs(prev => new Set([...prev, 'types']));
        }
        break;
      }
    }
  };

  const handleTabSelect = async (tab) => {
    setActiveTab(tab);
    if (loadedTabs.has(tab)) return;
    setTabLoading(true);
    try {
      await loadTabData(tab);
      setLoadedTabs(prev => new Set([...prev, tab]));
    } finally {
      setTabLoading(false);
    }
  };

  const loadProducts = async () => {
    if (productsLoaded || productsLoading) return;
    setProductsLoading(true);
    try {
      const res = await HttpNoData('/api/v1/calculateOrderCost/products', 'GET', jwt);
      const sorted = res.data?.sort((a, b) => a.priority - b.priority) ?? [];
      const fixed = await checkAndFixDuplicates(sorted, (id, p) =>
        `/api/v1/calculateOrderCost/products/priority/${id}/${p}`
      );
      setProducts(fixed);
      setProductsLoaded(true);
    } finally {
      setProductsLoading(false);
    }
  };

  const handlePriceNameChange = (id, newName) => {
    setPriceSettings((prev) =>
      prev.map((item) => (item.id === id ? { ...item, name: newName } : item)),
    );
  };

  const handlePriceValueChange = (id, newValue) => {
    setPriceSettings((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, price: { ...item.price, units: newValue } }
          : item,
      ),
    );
  };

  const updatePrice = async () => {
    for (let i = 0; i < priceSettings.length; i++) {
      const res = await HttpData(
        `/api/v1/calculateOrderCost/priceOnWeight/${priceSettings[i].id}`,
        "PUT",
        jwt,
        {
          id: priceSettings[i].id,
          name: priceSettings[i].name,
          price: priceSettings[i].price.units,
        },
      );
      if (!res.statusSuccessful) {
        alert(`Не получилось обновить: ${res.error}`);
        return;
      }
    }
    alert("Успешно сохранено");
  };

  const updatePriority = async (index, vector) => {
    if (
      (index === 0 && vector === "up") ||
      (additionsInOrder.length - 1 === index && vector === "down")
    ) {
      return;
    }

    const currentItem = additionsInOrder[index];
    let targetIndex;

    if (vector === "up") {
      targetIndex = index - 1;
    } else if (vector === "down") {
      targetIndex = index + 1;
    } else {
      alert("Ты что удумал?");
      return;
    }

    const targetItem = additionsInOrder[targetIndex];
    try {
      const updateCurrent = await HttpNoData(
        `/api/v1/calculateOrderCost/orders/additions/priority/${currentItem.id}/${targetItem.priority}`,
        "PUT",
        jwt,
      );

      const updateTarget = await HttpNoData(
        `/api/v1/calculateOrderCost/orders/additions/priority/${targetItem.id}/${currentItem.priority}`,
        "PUT",
        jwt,
      );

      if (updateCurrent.statusSuccessful && updateTarget.statusSuccessful) {
        setAdditionsInOrder((prev) => {
          const newArray = [...prev];
          newArray[index] = { ...newArray[index], priority: targetItem.priority };
          newArray[targetIndex] = { ...newArray[targetIndex], priority: currentItem.priority };
          return newArray.sort((a, b) => a.priority - b.priority);
        });
      } else {
        alert("Не удалось обновить приоритеты на сервере");
      }
    } catch (error) {
      console.error("Ошибка при обновлении приоритетов:", error);
      alert("Произошла ошибка при обновлении приоритетов");
    }
  };

  const updateProductCategoryPriority = async (index, vector) => {
    if (
      (index === 0 && vector === "up") ||
      (categories.length - 1 === index && vector === "down")
    ) {
      return;
    }

    const currentItem = categories[index];
    let targetIndex;

    if (vector === "up") {
      targetIndex = index - 1;
    } else if (vector === "down") {
      targetIndex = index + 1;
    } else {
      alert("Ты что удумал?");
      return;
    }

    const targetItem = categories[targetIndex];
    try {
      const updateCurrent = await HttpNoData(
        `/api/v1/calculateOrderCost/productCategories/priority/${currentItem.id}/${targetItem.priority}`,
        "PUT",
        jwt,
      );

      const updateTarget = await HttpNoData(
        `/api/v1/calculateOrderCost/productCategories/priority/${targetItem.id}/${currentItem.priority}`,
        "PUT",
        jwt,
      );

      if (updateCurrent.statusSuccessful && updateTarget.statusSuccessful) {
        setCategories((prev) => {
          const newArray = [...prev];
          newArray[index] = { ...newArray[index], priority: targetItem.priority };
          newArray[targetIndex] = { ...newArray[targetIndex], priority: currentItem.priority };
          return newArray.sort((a, b) => a.priority - b.priority);
        });
      } else {
        alert("Не удалось обновить приоритеты на сервере");
      }
    } catch (error) {
      console.error("Ошибка при обновлении приоритетов:", error);
      alert("Произошла ошибка при обновлении приоритетов");
    }
  };

  const updateTypePriority = async (index, vector) => {
    if (
      (index === 0 && vector === "up") ||
      (typeCategories.length - 1 === index && vector === "down")
    ) {
      return;
    }

    const currentItem = typeCategories[index];
    let targetIndex;

    if (vector === "up") {
      targetIndex = index - 1;
    } else if (vector === "down") {
      targetIndex = index + 1;
    } else {
      alert("Ты что удумал?");
      return;
    }

    const targetItem = typeCategories[targetIndex];
    try {
      const updateCurrent = await HttpNoData(
        `/api/v1/calculateOrderCost/typeProductCategories/priority/${currentItem.id}/${targetItem.priority}`,
        "PUT",
        jwt,
      );

      const updateTarget = await HttpNoData(
        `/api/v1/calculateOrderCost/typeProductCategories/priority/${targetItem.id}/${currentItem.priority}`,
        "PUT",
        jwt,
      );

      if (updateCurrent.statusSuccessful && updateTarget.statusSuccessful) {
        setTypeCategories((prev) => {
          const newArray = [...prev];
          newArray[index] = { ...newArray[index], priority: targetItem.priority };
          newArray[targetIndex] = { ...newArray[targetIndex], priority: currentItem.priority };
          return newArray.sort((a, b) => a.priority - b.priority);
        });
      } else {
        alert("Не удалось обновить приоритеты на сервере");
      }
    } catch (error) {
      console.error("Ошибка при обновлении приоритетов:", error);
      alert("Произошла ошибка при обновлении приоритетов");
    }
  };

  const arrayFixPriority = async (array, predicate) => {
    const temp = [
      ...array
        .sort((a, b) => a.priority - b.priority)
        .map((o, i) => {
          o.priority = i + 1;
          return o;
        }),
    ];

    for (let i = 0; i < temp.length; i++) {
      const element = temp[i];
      await HttpNoData(predicate(element.id, element.priority), "PUT", jwt);
    }

    return temp;
  };

  const toggleAdditionsProduct = async () => {
    if (!additionsProductOpen && !additionsProductLoaded) {
      const response = await HttpNoData('/api/v1/calculateOrderCost/products/additions', 'GET', jwt);
      setAdditionsInProduct(response.data ?? []);
      setAdditionsProductLoaded(true);
    }
    setAdditionsProductOpen(prev => !prev);
  };

  const createAdditionAsync = async () => {
    const response = await HttpData('/api/v1/calculateOrderCost/products/additions', 'POST', jwt, {
      name: newAdditionName,
      price: +newAdditionPrice,
      description: newAdditionDescription || null,
      isForExport: newAdditionIsForExport,
      hasQuantity: newAdditionHasQuantity,
    });

    if (response.statusSuccessful) {
      setAdditionsInProduct(prev => [...prev, response.data]);
      setNewAdditionName('');
      setNewAdditionPrice('');
      setNewAdditionDescription('');
      setNewAdditionIsForExport(true);
      setNewAdditionHasQuantity(false);
      setCreateAdditionType(null);
    } else {
      alert('Не получилось создать дополнение');
    }
  };

  const updateProductPriority = async (index, vector) => {
    if (
      (index === 0 && vector === "up") ||
      (products.length - 1 === index && vector === "down")
    ) {
      return;
    }

    const currentItem = products[index];
    let targetIndex;

    if (vector === "up") {
      targetIndex = index - 1;
    } else if (vector === "down") {
      targetIndex = index + 1;
    } else {
      alert("Ты что удумал?");
      return;
    }

    const targetItem = products[targetIndex];
    try {
      const updateCurrent = await HttpNoData(
        `/api/v1/calculateOrderCost/products/priority/${currentItem.id}/${targetItem.priority}`,
        "PUT",
        jwt,
      );

      const updateTarget = await HttpNoData(
        `/api/v1/calculateOrderCost/products/priority/${targetItem.id}/${currentItem.priority}`,
        "PUT",
        jwt,
      );

      if (updateCurrent.statusSuccessful && updateTarget.statusSuccessful) {
        setProducts((prev) => {
          const newArray = [...prev];
          newArray[index] = { ...newArray[index], priority: targetItem.priority };
          newArray[targetIndex] = { ...newArray[targetIndex], priority: currentItem.priority };
          return newArray.sort((a, b) => a.priority - b.priority);
        });
      } else {
        alert("Не удалось обновить приоритеты на сервере");
      }
    } catch (error) {
      console.error("Ошибка при обновлении приоритетов:", error);
      alert("Произошла ошибка при обновлении приоритетов");
    }
  };

  const productsByCategory = useMemo(() => {
    const grouped = {};
    categories.forEach((category) => {
      grouped[category.id] = products
        .filter((product) => product.categoryId === category.id)
        .sort((a, b) => a.priority - b.priority);
    });
    return grouped;
  }, [categories, products]);

  const currentId = editClass.includes(":") ? +editClass.split(":")[1] : 0;

  const tabSpinner = (
    <div className="text-center p-4">
      <Spinner animation="border" />
    </div>
  );

  return (
    <Stack className={styles.bodyMain} gap={3}>
      <Tabs activeKey={activeTab} onSelect={handleTabSelect} className="mb-3">

        {/* ── Цены / НДС ── */}
        <Tab eventKey="prices" title="Цены / НДС">
          <Row>
            <Col md={6}>
              <Stack gap={1}>
                {priceSettings.map((price, index) => (
                  <div key={price.id}>
                    <InputGroup className="mb-3">
                      <Form.Control
                        type="text"
                        value={priceSettings[index].name}
                        onChange={(e) => handlePriceNameChange(price.id, e.target.value)}
                        style={{ flex: 3 }}
                      />
                      <Form.Control
                        type="number"
                        value={priceSettings[index].price.units}
                        onChange={(e) => handlePriceValueChange(price.id, e.target.value)}
                        style={{ flex: 1 }}
                      />
                    </InputGroup>
                  </div>
                ))}
                <div className="d-flex justify-content-between">
                  <Button variant="success" onClick={() => setIsNds(true)}>
                    НДС %
                  </Button>
                  <Button variant="success" onClick={() => updatePrice()}>
                    Сохранить
                  </Button>
                </div>
                {isNds && (
                  <ModalNdsEdit
                    show={isNds}
                    onHide={() => setIsNds(false)}
                    jwt={jwt}
                  />
                )}
              </Stack>
            </Col>
          </Row>
        </Tab>

        {/* ── Типы и категории ── */}
        <Tab eventKey="types" title="Типы и категории">
          {activeTab === 'types' && tabLoading ? tabSpinner : (
            <Row>
              <Col md={6}>
                <h4>Типы</h4>
                <ListGroup>
                  {typeCategories.length > 0 ? (
                    typeCategories.map((o, index) => (
                      <ListGroup.Item key={o.id}>
                        <div className={styles.listItem}>
                          <span>{o.name}</span>
                          <Stack direction="horizontal" gap={1}>
                            <Button
                              variant="success"
                              disabled={index === 0}
                              onClick={() => updateTypePriority(index, "up")}
                            >
                              <FaAngleDoubleUp />
                            </Button>
                            <Button
                              variant="danger"
                              disabled={index === typeCategories.length - 1}
                              onClick={() => updateTypePriority(index, "down")}
                            >
                              <FaAngleDoubleDown />
                            </Button>
                            <Button
                              variant="warning"
                              onClick={() => setEditClass(`type:${o.id}`)}
                            >
                              <MdEdit />
                            </Button>
                          </Stack>
                        </div>
                      </ListGroup.Item>
                    ))
                  ) : (
                    <Alert variant="warning">Пусто</Alert>
                  )}
                  <Button variant="success" onClick={() => setCreateClass("type")}>
                    <HiPlus />
                  </Button>
                </ListGroup>
              </Col>
              <Col md={6}>
                <h4>Категории</h4>
                <ListGroup>
                  {categories.length > 0 ? (
                    categories.map((category, catIndex) => (
                      <ListGroup.Item key={category.id}>
                        <div className={styles.listItem}>
                          <span>{category.name}</span>
                          <Stack direction="horizontal" gap={1}>
                            <Button
                              variant="success"
                              disabled={catIndex === 0}
                              onClick={() => updateProductCategoryPriority(catIndex, "up")}
                            >
                              <FaAngleDoubleUp />
                            </Button>
                            <Button
                              variant="danger"
                              disabled={catIndex === categories.length - 1}
                              onClick={() => updateProductCategoryPriority(catIndex, "down")}
                            >
                              <FaAngleDoubleDown />
                            </Button>
                            <Button
                              variant="warning"
                              onClick={() => setEditClass(`category:${category.id}`)}
                            >
                              <MdEdit />
                            </Button>
                          </Stack>
                        </div>
                      </ListGroup.Item>
                    ))
                  ) : (
                    <Alert variant="warning">Пусто</Alert>
                  )}
                  <Button variant="success" onClick={() => setCreateClass("category")}>
                    <HiPlus />
                  </Button>
                </ListGroup>
              </Col>
            </Row>
          )}
        </Tab>

        {/* ── Справочники ── */}
        <Tab eventKey="references" title="Хар-ки для цеха">
          {activeTab === 'references' && tabLoading ? tabSpinner : (
            <>
              <Row>
                <Col md={6}>
                  <h4>Подтипы</h4>
                  <ListGroup>
                    <ListGroup.Item>
                      <div className={styles.listItem}>
                        <div>
                          <span>Название медали</span>
                        </div>
                        <Stack direction="horizontal" gap={1}>
                          <Button variant="secondary" disabled>
                            <MdEdit />
                          </Button>
                        </Stack>
                      </div>
                    </ListGroup.Item>
                    {subtypes.length > 0 ? (
                      subtypes
                        .filter((subtype) => subtype.id !== 3)
                        .map((subtype) => (
                          <ListGroup.Item key={subtype.id}>
                            <div className={styles.listItem}>
                              <div>
                                <span>{subtype.name}</span>
                              </div>
                              <Stack direction="horizontal" gap={1}>
                                <Button
                                  variant="warning"
                                  onClick={() => setEditClass(`subtype:${subtype.id}`)}
                                >
                                  <MdEdit />
                                </Button>
                              </Stack>
                            </div>
                          </ListGroup.Item>
                        ))
                    ) : (
                      <Alert variant="warning">Пусто</Alert>
                    )}
                    <Button variant="success" onClick={() => setCreateClass("subtype")}>
                      <HiPlus />
                    </Button>
                  </ListGroup>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md={6}>
                  <h4>Нанесения</h4>
                  <ListGroup>
                    {coatings.length > 0 ? (
                      coatings.map((coating) => (
                        <ListGroup.Item key={coating.id}>
                          <div className={styles.listItem}>
                            <div>
                              <span>{coating.name}</span>
                            </div>
                            <Stack direction="horizontal" gap={1}>
                              <Button
                                variant="warning"
                                onClick={() => setEditClass(`coating:${coating.id}`)}
                              >
                                <MdEdit />
                              </Button>
                            </Stack>
                          </div>
                        </ListGroup.Item>
                      ))
                    ) : (
                      <Alert variant="warning">Пусто</Alert>
                    )}
                    <Button variant="success" onClick={() => setCreateClass("coating")}>
                      <HiPlus />
                    </Button>
                  </ListGroup>
                </Col>
                <Col md={6}>
                  <h4>Материалы продукции</h4>
                  <ListGroup>
                    {materials.length > 0 ? (
                      materials.map((material) => (
                        <ListGroup.Item key={material.id}>
                          <div className={styles.listItem}>
                            <div>
                              <span>{material.name}</span>
                            </div>
                            <Stack direction="horizontal" gap={1}>
                              <Button
                                variant="warning"
                                onClick={() => setEditClass(`material:${material.id}`)}
                              >
                                <MdEdit />
                              </Button>
                            </Stack>
                          </div>
                        </ListGroup.Item>
                      ))
                    ) : (
                      <Alert variant="warning">Пусто</Alert>
                    )}
                    <Button variant="success" onClick={() => setCreateClass("material")}>
                      <HiPlus />
                    </Button>
                  </ListGroup>
                </Col>
              </Row>
            </>
          )}
        </Tab>

        {/* ── Допики заказов ── */}
        <Tab eventKey="orderAdditions" title="Допики заказов">
          {activeTab === 'orderAdditions' && tabLoading ? tabSpinner : (
            <>
            <Row>
              <Col md>
                <h4>Дополнения к заказу</h4>
                <ListGroup>
                  {additionsInOrder.length > 0 ? (
                    additionsInOrder.map((o, index) => (
                      <ListGroup.Item key={o.id}>
                        <div className={styles.listItem}>
                          <span>{o.name}</span>
                          <Stack direction="horizontal" gap={1}>
                            <Button
                              variant="success"
                              disabled={index === 0}
                              onClick={() => updatePriority(index, "up")}
                            >
                              <FaAngleDoubleUp />
                            </Button>
                            <Button
                              variant="danger"
                              disabled={index === additionsInOrder.length - 1}
                              onClick={() => updatePriority(index, "down")}
                            >
                              <FaAngleDoubleDown />
                            </Button>
                            <Button
                              variant="warning"
                              onClick={() => setEditClass(`addition:${o.id}`)}
                            >
                              <MdEdit />
                            </Button>
                          </Stack>
                        </div>
                      </ListGroup.Item>
                    ))
                  ) : (
                    <Alert variant="warning">Пусто</Alert>
                  )}
                  <Button variant="success" onClick={() => setCreateClass("addition")}>
                    <HiPlus />
                  </Button>
                </ListGroup>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md={6}>
                <h5>% наценки на доставку</h5>
                <InputGroup>
                  <Form.Control
                    type="number"
                    value={deliveryMarkupInput}
                    onChange={(e) => setDeliveryMarkupInput(+e.target.value)}
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                  <Button
                    variant={deliveryMarkupInput === deliveryMarkupPercent ? "outline-secondary" : "success"}
                    onClick={async () => {
                      const res = await HttpData(
                        `/api/v1/calculateOrderCost/deliveryMarkup/${deliveryMarkupInput}`,
                        'PUT',
                        jwt
                      );
                      if (res.statusSuccessful) setDeliveryMarkupPercent(deliveryMarkupInput);
                      else alert('Не удалось сохранить');
                    }}
                  >
                    Сохранить
                  </Button>
                </InputGroup>
              </Col>
            </Row>
            </>
          )}
        </Tab>

        {/* ── Допики продуктов ── */}
        <Tab eventKey="productAdditions" title="Допики продуктов">
          <Row>
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h4 className="mb-0">Допики обычных продуктов</h4>
                <Stack direction="horizontal" gap={1}>
                  <Button variant="success" size="sm" onClick={() => setCreateAdditionType('product')}>
                    <HiPlus />
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={toggleAdditionsProduct}>
                    {additionsProductOpen ? '▲' : '▼'}
                  </Button>
                </Stack>
              </div>
              {additionsProductOpen && (
                <ListGroup>
                  {!additionsProductLoaded ? (
                    <ListGroup.Item className="text-muted">Загрузка...</ListGroup.Item>
                  ) : additionsInProduct.length > 0 ? (
                    additionsInProduct.map((addition) => (
                      <ListGroup.Item key={addition.id}>
                        <div className={styles.listItem}>
                          <span className="small">{addition.name} — {addition.price}₽</span>
                          <Button variant="warning" size="sm" onClick={() => setEditingAdditionProduct(addition)}>
                            <MdEdit />
                          </Button>
                        </div>
                      </ListGroup.Item>
                    ))
                  ) : (
                    <Alert variant="warning" className="mb-0">Нет дополнений</Alert>
                  )}
                </ListGroup>
              )}
            </Col>
          </Row>
        </Tab>

        {/* ── Продукты ── */}
        <Tab eventKey="products" title="Продукты">
          {activeTab === 'products' && tabLoading ? tabSpinner : (
            <Row>
              <Col>
                <Row>
                  {categories.map((category, catIndex) => {
                    const isExpanded = expandedCategories.has(category.id);
                    const toggleCategory = () => {
                      setExpandedCategories(prev => {
                        const next = new Set(prev);
                        if (next.has(category.id)) next.delete(category.id);
                        else next.add(category.id);
                        return next;
                      });
                      if (!expandedCategories.has(category.id)) loadProducts();
                    };
                    return (
                      <Col md={6} key={category.id} className="mb-3">
                        <div className="border rounded p-3 h-100">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">{category.name}</h6>
                            <Stack direction="horizontal" gap={1}>
                              <Button
                                variant="success"
                                disabled={catIndex === 0}
                                onClick={() => updateProductCategoryPriority(catIndex, "up")}
                                title="Поднять выше"
                              >
                                <FaAngleDoubleUp />
                              </Button>
                              <Button
                                variant="danger"
                                disabled={catIndex === categories.length - 1}
                                onClick={() => updateProductCategoryPriority(catIndex, "down")}
                                title="Опустить ниже"
                              >
                                <FaAngleDoubleDown />
                              </Button>
                              <Button
                                variant="warning"
                                onClick={() => setEditClass(`category:${category.id}`)}
                                title="Редактировать категорию"
                              >
                                <MdEdit />
                              </Button>
                              <Button
                                variant="primary"
                                onClick={() => setCreateClass(`product:${category.id}`)}
                                title="Добавить продукт в эту категорию"
                              >
                                <HiPlus />
                              </Button>
                              <Button
                                variant="outline-secondary"
                                onClick={toggleCategory}
                                title={isExpanded ? "Свернуть" : "Развернуть"}
                              >
                                {isExpanded ? '▲' : '▼'}
                              </Button>
                            </Stack>
                          </div>

                          {isExpanded && (
                            productsLoading ? (
                              <div className="text-center py-2">
                                <Spinner animation="border" size="sm" />
                              </div>
                            ) : (
                              <ListGroup>
                                {productsByCategory[category.id]?.length > 0 ? (
                                  productsByCategory[category.id].map((product) => {
                                    const globalIndex = products.findIndex(p => p.id === product.id);
                                    return (
                                      <ListGroup.Item key={product.id}>
                                        <div className="d-flex justify-content-between align-items-center">
                                          <span className="small">{product.name}</span>
                                          <Stack direction="horizontal" gap={1}>
                                            <Button
                                              variant="success"
                                              disabled={globalIndex === 0}
                                              onClick={() => updateProductPriority(globalIndex, "up")}
                                              title="Поднять выше"
                                            >
                                              <FaAngleDoubleUp />
                                            </Button>
                                            <Button
                                              variant="danger"
                                              disabled={globalIndex === products.length - 1}
                                              onClick={() => updateProductPriority(globalIndex, "down")}
                                              title="Опустить ниже"
                                            >
                                              <FaAngleDoubleDown />
                                            </Button>
                                            <Button
                                              variant="warning"
                                              onClick={() => setEditClass(`product:${product.id}`)}
                                              title="Редактировать продукт"
                                            >
                                              <MdEdit />
                                            </Button>
                                          </Stack>
                                        </div>
                                      </ListGroup.Item>
                                    );
                                  })
                                ) : (
                                  <Alert variant="info" className="small mb-0 py-1">
                                    В этой категории нет продуктов
                                  </Alert>
                                )}
                              </ListGroup>
                            )
                          )}
                        </div>
                      </Col>
                    );
                  })}
                </Row>
                <div className="mt-3">
                  <Button variant="success" onClick={() => setCreateClass("category")}>
                    <HiPlus /> Добавить новую категорию
                  </Button>
                </div>
              </Col>
            </Row>
          )}
        </Tab>

        {/* TODO(wholesale-removal): вкладка "Оптовые" убрана из админки, бэк/компоненты
            (OptProductModalCreate/Edit, OptPricingModal, AdditionInOptProductEdit,
            OptProductsController и т.д.) остались как кандидаты на снос. */}

      </Tabs>

      {/* ── Модалки добавления продуктов ── */}
      {editingAdditionProduct && (
        <Modal show onHide={() => setEditingAdditionProduct(null)}>
          <Modal.Header closeButton>
            <Modal.Title>{editingAdditionProduct.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            <ListGroup>
              <AdditionInProductEdit
                key={editingAdditionProduct.id}
                jwt={jwt}
                addition={editingAdditionProduct}
                onDeleted={() => {
                  setAdditionsInProduct(prev => prev.filter(a => a.id !== editingAdditionProduct.id));
                  setEditingAdditionProduct(null);
                }}
              />
            </ListGroup>
          </Modal.Body>
        </Modal>
      )}

      <Modal show={!!createAdditionType} onHide={() => setCreateAdditionType(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Новый допик (обычный продукт)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Название</Form.Label>
            <Form.Control
              value={newAdditionName}
              onChange={e => setNewAdditionName(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Цена</Form.Label>
            <Form.Control
              type="number"
              value={newAdditionPrice}
              onChange={e => setNewAdditionPrice(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Описание</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={newAdditionDescription}
              onChange={e => setNewAdditionDescription(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Участвует в выгрузке"
              checked={newAdditionIsForExport}
              onChange={e => setNewAdditionIsForExport(e.target.checked)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="С количеством"
              checked={newAdditionHasQuantity}
              onChange={e => setNewAdditionHasQuantity(e.target.checked)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCreateAdditionType(null)}>Отмена</Button>
          <Button
            variant="success"
            disabled={!newAdditionName || !newAdditionPrice}
            onClick={() => createAdditionAsync()}
          >
            Создать
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Категории ── */}
      {createClass === 'category' && (
        <CategoryModalCreate
          show={true}
          onCreated={c => setCategories([...categories, c])}
          onHide={() => setCreateClass()}
          types={typeCategories}
          jwt={jwt}
        />
      )}

      {editClass.includes('category') && (
        <CategoryModalEdit
          show={true}
          jwt={jwt}
          types={typeCategories}
          onDeleted={() => {
            setCategories([...categories.filter(f => f.id !== currentId)]);
            setEditClass('');
          }}
          onHide={() => setEditClass('')}
          onUpdated={(u) => {
            const index = categories.findIndex(f => f.id === currentId);
            categories[index] = u;
            setCategories([...categories]);
          }}
          category={categories.find(f => f.id === currentId)}
        />
      )}

      {/* ── Продукты ── */}
      {createClass?.startsWith('product:') && (
        <ProductModalCreate
          show={true}
          onCreated={(c) => setProducts([...products, c])}
          onHide={() => setCreateClass()}
          categories={categories}
          subtypes={subtypes}
          materials={materials}
          preselectedCategoryId={+createClass.split(":")[1]}
          jwt={jwt}
        />
      )}

      {editClass.includes('product:') && !editClass.includes('opt-product') && (
        <ProductModalEdit
          show={true}
          jwt={jwt}
          categories={categories}
          subtypes={subtypes}
          materials={materials}
          onDeleted={() => {
            setProducts([...products.filter((f) => f.id !== currentId)]);
            setEditClass("");
          }}
          onHide={() => setEditClass("")}
          onUpdated={(u) => {
            const index = products.findIndex((f) => f.id === currentId);
            products[index] = u;
            setProducts([...products]);
          }}
          product={products.find((f) => f.id === currentId)}
        />
      )}

      {/* ── Допики заказов ── */}
      {createClass === 'addition' && (
        <AdditionInOrderModalCreate
          show={true}
          onCreated={c => setAdditionsInOrder([...additionsInOrder, c])}
          onHide={() => setCreateClass()}
          jwt={jwt}
        />
      )}

      {editClass.includes('addition') && (
        <AdditionOrderModalEdit
          show={true}
          jwt={jwt}
          onDeleted={() => {
            setAdditionsInOrder([...additionsInOrder.filter(f => f.id !== currentId)]);
            setEditClass('');
          }}
          onHide={() => setEditClass('')}
          onUpdated={(u) => {
            const index = additionsInOrder.findIndex(f => f.id === currentId);
            additionsInOrder[index] = u;
            setAdditionsInOrder([...additionsInOrder]);
            setEditClass('');
          }}
          addition={additionsInOrder.find(f => f.id === currentId)}
        />
      )}

      {/* ── Нанесения ── */}
      {createClass === 'coating' && (
        <CoatingModalCreate
          show={true}
          jwt={jwt}
          onHide={() => setCreateClass()}
          onCreated={(coating) => setCoatings([...coatings, coating])}
        />
      )}

      {editClass.includes("coating") && (
        <CoatingModalEdit
          show={true}
          jwt={jwt}
          onHide={() => setEditClass("")}
          onUpdated={(updatedCoating) => {
            const index = coatings.findIndex((f) => f.id === updatedCoating.id);
            if (index !== -1) {
              const newCoatings = [...coatings];
              newCoatings[index] = updatedCoating;
              setCoatings(newCoatings);
            }
          }}
          onDeleted={() => {
            const id = +editClass.split(":")[1];
            setCoatings(coatings.filter((f) => f.id !== id));
            setEditClass("");
          }}
          coating={coatings.find((f) => f.id === +editClass.split(":")[1])}
        />
      )}

      {/* ── Материалы ── */}
      {createClass === "material" && (
        <MaterialModalCreate
          show={true}
          jwt={jwt}
          onHide={() => setCreateClass()}
          onCreated={(material) => setMaterials([...materials, material])}
        />
      )}

      {editClass.includes("material") && (
        <MaterialModalEdit
          show={true}
          jwt={jwt}
          onHide={() => setEditClass("")}
          onUpdated={(updatedMaterial) => {
            const index = materials.findIndex((f) => f.id === updatedMaterial.id);
            if (index !== -1) {
              const newMaterials = [...materials];
              newMaterials[index] = updatedMaterial;
              setMaterials(newMaterials);
            }
          }}
          onDeleted={() => {
            setMaterials(materials.filter((f) => f.id !== currentId));
            setEditClass("");
          }}
          material={materials.find((f) => f.id === currentId)}
        />
      )}

      {/* ── Типы ── */}
      {createClass === "type" && (
        <TypeModalCreate
          show={true}
          onCreated={(c) => setTypeCategories([...typeCategories, c])}
          onHide={() => setCreateClass()}
          jwt={jwt}
        />
      )}

      {editClass.includes("type") && !editClass.includes("subtype") && (
        <TypeModalEdit
          show={true}
          jwt={jwt}
          onDeleted={() => {
            setTypeCategories([...typeCategories.filter((f) => f.id !== currentId)]);
            setEditClass("");
          }}
          onHide={() => setEditClass("")}
          onUpdated={(u) => {
            const index = typeCategories.findIndex((f) => f.id === currentId);
            typeCategories[index] = u;
            setTypeCategories([...typeCategories]);
          }}
          type={typeCategories.find((f) => f.id === currentId)}
        />
      )}

      {/* ── Подтипы ── */}
      {createClass === "subtype" && (
        <ProductSubtypeModalCreate
          show={true}
          jwt={jwt}
          onHide={() => setCreateClass()}
          onCreated={(subtype) => setSubtypes([...subtypes, subtype])}
        />
      )}

      {editClass.includes("subtype") && (
        <ProductSubtypeModalEdit
          show={true}
          jwt={jwt}
          onHide={() => setEditClass("")}
          onUpdated={(updatedSubtype) => {
            const index = subtypes.findIndex((f) => f.id === updatedSubtype.id);
            if (index !== -1) {
              const newSubtypes = [...subtypes];
              newSubtypes[index] = updatedSubtype;
              setSubtypes(newSubtypes);
            }
          }}
          onDeleted={() => {
            setSubtypes(subtypes.filter((f) => f.id !== currentId));
            setEditClass("");
          }}
          subtype={subtypes.find((f) => f.id === currentId)}
        />
      )}
    </Stack>
  );
}
